/* ----------------------------------------------------------------------------
 * Content-action flow model (P3 Lane 3D) — the PURE, dependency-free logic
 * behind the copilot's "propose → prepare → guided brief → draft handoff"
 * conversation. Extracted here so the whole flow is unit-testable under
 * `node --test --experimental-strip-types` (this repo's DOM-free FE test
 * convention); ContentActionPanel is a thin view over these helpers.
 *
 * CONNECTOR GATE (invariant 5) is enforced structurally: the only control verbs
 * this flow can ever surface are enumerated in ALLOWED_CONTROL_VERBS, and that
 * set contains NO publish / schedule / send verb. controlsForPhase() can only
 * return a subset of it, so an active publish/send control is unrepresentable.
 * -------------------------------------------------------------------------- */

import type {
  BlogDraft,
  BriefChannel,
  BriefQuestion,
  ConnectorGate,
  ContentAction,
  ContentActionBrief,
  EvidenceRef,
} from '../../data/copilot.ts';

/** The phases of the copilot content-action conversation. */
export type ContentActionPhase =
  | 'idle' // entry affordance shown
  | 'proposing' // awaiting the recommend/propose response
  | 'domain_choice' // >1 domain, none active — pick which
  | 'no_signal' // honest no-data / no-domain reply, no action
  | 'proposal' // a concrete action proposed — "Prepare it?"
  | 'accepting' // awaiting the recommend/accept response
  | 'brief' // guided Q&A, one missing field at a time
  | 'review' // review all answers before creating the draft
  | 'submitting' // handing the brief to the draft intake
  | 'draft_ready' // draft created — open to review/revise/approve
  | 'handoff_pending' // brief ready, intake not yet wired (fail-closed, honest)
  | 'needs_detail' // intake accepted the shape but still needs a few fields (honest)
  | 'blog_unavailable' // blog/article intent fails HONESTLY — on the roadmap, NOT an error
  | 'blog_draft' // populated long-form blog_article draft (HTTP 200) — the P1 happy path
  | 'error'; // something failed — nothing changed

/** Collected answers keyed by brief field. */
export type BriefAnswers = Record<string, string>;

/**
 * Every control verb the flow may surface. Deliberately excludes publish,
 * schedule and send: connectors are gated, so those actions cannot be rendered.
 */
export const ALLOWED_CONTROL_VERBS = [
  'suggest', // start: ask Aura for a content action
  'pick-domain', // choose which domain to suggest for
  'prepare', // accept the proposal
  'dismiss', // not now
  'answer', // answer the current brief question
  'back', // previous question
  'next', // next question
  'skip', // leave an optional field blank
  'revise', // go back and edit answers
  'create-draft', // submit the brief to the intake (creates a DRAFT only)
  'open-draft', // navigate to the created draft to review
  'copy-brief', // copy the prepared brief text
  'copy-text', // copy the blog draft body as plain/markdown text
  'copy-html', // copy the blog draft body as HTML
  'repurpose', // hand the blog draft to the existing social path (client stub — NO generation)
  'retry', // retry after an error
] as const;

export type ControlVerb = (typeof ALLOWED_CONTROL_VERBS)[number];

/** Verbs that must NEVER appear anywhere in this flow (connectors gated). */
export const FORBIDDEN_CONTROL_VERBS = ['publish', 'schedule', 'send'] as const;

/** The controls available in a given phase — always a subset of the allowed set. */
export function controlsForPhase(phase: ContentActionPhase): ControlVerb[] {
  switch (phase) {
    case 'idle':
      return ['suggest'];
    case 'proposing':
    case 'accepting':
    case 'submitting':
      return [];
    case 'domain_choice':
      return ['pick-domain', 'dismiss'];
    case 'no_signal':
      return ['dismiss'];
    case 'proposal':
      return ['prepare', 'dismiss'];
    case 'brief':
      return ['answer', 'back', 'next', 'skip', 'dismiss'];
    case 'review':
      return ['create-draft', 'revise', 'dismiss'];
    case 'draft_ready':
      return ['open-draft', 'dismiss'];
    case 'handoff_pending':
      return ['copy-brief', 'dismiss'];
    case 'needs_detail':
      return ['answer', 'copy-brief', 'dismiss'];
    case 'blog_unavailable':
      // On the roadmap, NOT an error: keep the brief (copy) or dismiss. No retry.
      return ['copy-brief', 'dismiss'];
    case 'blog_draft':
      // Populated long-form draft: copy text / copy HTML / repurpose to social.
      // NO publish/schedule/send — connectors stay gated (invariant 5).
      return ['copy-text', 'copy-html', 'repurpose', 'dismiss'];
    case 'error':
      return ['retry', 'dismiss'];
    default:
      return [];
  }
}

/** True iff `verb` is a control this flow is ever allowed to render. */
export function isAllowedControl(verb: string): verb is ControlVerb {
  return (ALLOWED_CONTROL_VERBS as readonly string[]).includes(verb);
}

// ---- propose-response classification --------------------------------------

/**
 * Classify a /ai/recommend PROPOSE response into the flow phase it drives.
 * Pure mirror of copilot.ts isRecommendDomainChoice/recommendHasProposal so the
 * routing is unit-testable without loading the api client. Takes only the plain
 * fields it reads, so any response shape can be classified.
 */
export function classifyProposePhase(res: {
  needs_domain_choice?: boolean;
  can_prepare?: boolean;
  proposed_action?: unknown;
}): 'domain_choice' | 'proposal' | 'no_signal' {
  if (res.needs_domain_choice === true) return 'domain_choice';
  if (res.can_prepare === true && res.proposed_action != null) return 'proposal';
  return 'no_signal';
}

// ---- guided-brief stepping -------------------------------------------------

/** The ordered questions to ask — one missing brief field per step. */
export function briefSteps(questions: BriefQuestion[]): BriefQuestion[] {
  return questions.filter((q) => typeof q.field === 'string' && q.field !== '');
}

/** The current step, or null when the index is past the end. */
export function currentStep(
  questions: BriefQuestion[],
  index: number,
): BriefQuestion | null {
  const steps = briefSteps(questions);
  return index >= 0 && index < steps.length ? steps[index] : null;
}

export function isLastStep(questions: BriefQuestion[], index: number): boolean {
  const steps = briefSteps(questions);
  return steps.length > 0 && index === steps.length - 1;
}

/** Progress label, e.g. "Question 2 of 6". */
export function stepProgressLabel(questions: BriefQuestion[], index: number): string {
  const steps = briefSteps(questions);
  const n = Math.min(index + 1, steps.length);
  return 'Question ' + n + ' of ' + steps.length;
}

/**
 * Merge the collected answers into the §2.7 content-action, preserving the
 * server-filled channel/topic/evidence/domain and recomputing which fields (if
 * any) remain empty. Optional fields may be left blank — they simply stay empty.
 */
export function mergeBriefAnswers(
  action: ContentAction,
  answers: BriefAnswers,
): ContentAction {
  const brief: ContentActionBrief = { ...action.brief };
  for (const key of Object.keys(answers)) {
    if (key in brief) {
      const v = answers[key];
      (brief as unknown as Record<string, string>)[key] = typeof v === 'string' ? v.trim() : '';
    }
  }
  return {
    ...action,
    brief,
    missing_brief_fields: emptyBriefFields(brief),
  };
}

/** The §2.7 brief fields still empty, in the pinned deterministic order. */
export function emptyBriefFields(brief: ContentActionBrief): string[] {
  const order: (keyof ContentActionBrief)[] = [
    'channel',
    'objective',
    'audience',
    'topic',
    'tone',
    'format',
    'cta',
    'timing_hint',
  ];
  return order.filter((f) => {
    const v = brief[f];
    return typeof v !== 'string' || v.trim() === '';
  });
}

/** A human-readable label for a brief field (review + copy). */
export function briefFieldLabel(field: string): string {
  const map: Record<string, string> = {
    channel: 'Channel',
    objective: 'Objective',
    audience: 'Audience',
    topic: 'Topic',
    tone: 'Tone',
    format: 'Format',
    cta: 'Call to action',
    timing_hint: 'Timing',
  };
  return map[field] ?? field;
}

/** The review rows (label + value) for the completed brief, in pinned order. */
export function briefReviewRows(
  brief: ContentActionBrief,
): { field: string; label: string; value: string }[] {
  const order: (keyof ContentActionBrief)[] = [
    'channel',
    'topic',
    'objective',
    'audience',
    'tone',
    'format',
    'cta',
    'timing_hint',
  ];
  return order.map((f) => ({
    field: f,
    label: briefFieldLabel(f),
    value: (brief[f] ?? '').toString().trim(),
  }));
}

/** A plain-text rendering of the prepared brief (for copy-brief). */
export function briefToPlainText(action: ContentAction): string {
  const lines: string[] = [];
  lines.push('Content brief — prepared by Aura (draft only)');
  lines.push('Domain: ' + action.domain.hostname);
  for (const row of briefReviewRows(action.brief)) {
    if (row.value !== '') lines.push(row.label + ': ' + row.value);
  }
  if (action.evidence.length > 0) {
    lines.push('');
    lines.push('Evidence:');
    for (const ref of action.evidence) {
      lines.push('- [' + ref.source_label + '] ' + ref.summary);
    }
  }
  return lines.join('\n');
}

/**
 * A plain-text rendering of the PRESERVED blog recommendation, for copy when the
 * Blog Draft isn't available yet. Honest header (on the roadmap — NOT a ready
 * draft) + topic + domain + the SEO evidence, so the user keeps the brief for
 * when the channel ships. Reuses the same evidence formatting as briefToPlainText.
 */
export function unavailableBriefText(action: ContentAction, roadmapNote?: string): string {
  const lines: string[] = [];
  lines.push('Blog Draft — not available yet (on the roadmap; nothing was created)');
  lines.push('Domain: ' + action.domain.hostname);
  lines.push('Channel: ' + (action.brief.channel || 'blog'));
  if (action.brief.topic.trim() !== '') lines.push('Topic: ' + action.brief.topic.trim());
  if (action.evidence.length > 0) {
    lines.push('');
    lines.push('SEO evidence (preserved):');
    for (const ref of action.evidence) {
      lines.push('- [' + ref.source_label + '] ' + ref.summary);
    }
  }
  if (roadmapNote && roadmapNote.trim() !== '') {
    lines.push('');
    lines.push(roadmapNote.trim());
  }
  return lines.join('\n');
}

// ---- connector gate --------------------------------------------------------

/**
 * A connector gate is honoured only when it is fully closed: status 'gated' and
 * every channel (live/publish/schedule/send) false. Any deviation is treated as
 * NOT gated so the UI refuses to proceed rather than risk an active action.
 */
export function isFullyGated(c: ConnectorGate | null | undefined): boolean {
  if (!c) return false;
  return (
    c.status === 'gated' &&
    c.live === false &&
    c.publish === false &&
    c.schedule === false &&
    c.send === false
  );
}

/** The view model for the gated-connectors notice (text only, no controls). */
export function connectorGateNotice(c: ConnectorGate | null | undefined): {
  gated: boolean;
  note: string;
} {
  const note =
    c && typeof c.note === 'string' && c.note.trim() !== ''
      ? c.note
      : 'Draft only — publishing and sending are not connected yet.';
  return { gated: isFullyGated(c), note };
}

// ---- evidence presentation -------------------------------------------------

/** Source-labelled evidence lines for the proposal card (stored rows only). */
export function evidenceLines(evidence: EvidenceRef[]): { label: string; summary: string }[] {
  return (evidence ?? [])
    .filter((e) => e && typeof e.summary === 'string' && e.summary !== '')
    .map((e) => ({ label: e.source_label || 'Evidence', summary: e.summary }));
}

/** The channel a proposal targets, normalised for display. */
export function channelLabel(channel: BriefChannel | string): string {
  const map: Record<string, string> = {
    social: 'social post',
    blog: 'blog post',
    newsletter: 'newsletter',
  };
  return map[channel] ?? String(channel);
}

// ---- draft-intake handoff (reconciled to the merged 3B/3C routes) ----------

/** The two existing draft-intake resources an accepted action routes into. */
export type IntakeModule = 'content-studio' | 'marketing';

/**
 * The EXISTING routes the merged 3B/3C intakes live on. There is NO dedicated
 * `/from-brief` sub-route: 3B detects an Aura action inside the ordinary
 * `POST /v1/social/content` body; 3C detects it on `POST /v1/marketing/campaigns`.
 * This is the single place to reconcile if either lane re-pins its route.
 *   3B StudioIntakeService::isAuraAction  — body.content_action.source==='aura_action'
 *   3C CampaignsController::create        — body.source==='aura_action' (top level)
 */
export const DRAFT_INTAKE_PATHS: Record<IntakeModule, string> = {
  'content-studio': '/social/content',
  marketing: '/marketing/campaigns',
};

/**
 * Build the POST body the merged intake expects for a completed §2.7 brief.
 *  - content-studio (3B): the action is nested under `content_action` (its
 *    `source:'aura_action'` is what StudioIntakeService::isAuraAction detects).
 *  - marketing (3C): `source:'aura_action'` MUST sit at the TOP LEVEL (that is
 *    what CampaignsController::create switches on) with the action nested in
 *    `content_action`.
 * A top-level `answers` map (non-empty values only) is carried on BOTH — each
 * controller merges it over the pre-filled brief, so a late/extra answer (e.g. a
 * newsletter's required subject/message) reaches the server WITHOUT being
 * fabricated into the §2.7 brief itself. Nothing here publishes/schedules/sends.
 */
export function buildIntakeBody(
  action: ContentAction,
  intakeModule: IntakeModule,
  answers: Record<string, string> = {},
): Record<string, unknown> {
  const clean: Record<string, string> = {};
  for (const key of Object.keys(answers)) {
    const v = answers[key];
    if (typeof v === 'string' && v.trim() !== '') clean[key] = v.trim();
  }
  if (intakeModule === 'marketing') {
    return { source: 'aura_action', content_action: action, answers: clean };
  }
  return { content_action: action, answers: clean };
}

/** The intake response fields we read (3B `created` / 3C `draft_created`, or a
 *  200 advisory `needs_brief` / `needs_audience`). Success envelope is returned
 *  directly by ControllerSupport::respond (no wrapper). */
export interface IntakeResponseShape {
  outcome?: string;
  item?: { id?: number; review_status?: string; publish_state?: string };
  campaign?: { id?: number; status?: string };
  edit_url?: string;
  url?: string;
  id?: number;
  draft_id?: number;
  questions?: BriefQuestion[];
  available_segments?: { id: number; name: string }[];
  note?: string;
}

/** Still-needed detail the server asked for (honest; NOT a fabricated draft). */
export interface DraftFollowUp {
  reason: 'needs_brief' | 'needs_audience';
  questions: BriefQuestion[];
  segments: { id: number; name: string }[];
  note: string;
}

/** The outcome of handing a completed §2.7 brief to the existing draft intake. */
export interface DraftHandoff {
  /** 'created' -> a real draft exists at `url`; 'needs_detail' -> the server
   *  needs a few more fields (honest); 'pending' -> intake not wired here. */
  status: 'created' | 'pending' | 'needs_detail';
  draft_id?: number;
  url?: string;
  intake_module: IntakeModule;
  followUp?: DraftFollowUp;
}

/**
 * Map a merged-intake response to a handoff outcome (pure; no navigation, no
 * fabrication). `created`/`draft_created` -> a real draft (edit_url + id).
 * `needs_brief`/`needs_audience` -> `needs_detail` carrying the server's own
 * remaining questions/segments (honest; distinct from the fail-closed
 * "unwired" pending). Any other 2xx that still names a draft url -> created;
 * otherwise -> pending.
 */
export function classifyIntakeResponse(
  res: IntakeResponseShape | undefined,
  intakeModule: IntakeModule,
): DraftHandoff {
  const outcome = (res?.outcome ?? '').toString();
  if (outcome === 'created' || outcome === 'draft_created') {
    const url = res?.edit_url ?? res?.url;
    const draftId = res?.item?.id ?? res?.campaign?.id ?? res?.draft_id ?? res?.id;
    if (url) return { status: 'created', url, draft_id: draftId, intake_module: intakeModule };
    return { status: 'pending', draft_id: draftId, intake_module: intakeModule };
  }
  if (outcome === 'needs_brief' || outcome === 'needs_audience') {
    return {
      status: 'needs_detail',
      intake_module: intakeModule,
      followUp: {
        reason: outcome,
        questions: Array.isArray(res?.questions) ? res!.questions! : [],
        segments: Array.isArray(res?.available_segments) ? res!.available_segments! : [],
        note:
          typeof res?.note === 'string' && res!.note!.trim() !== ''
            ? res!.note!
            : 'A few more details are needed before I can prepare the draft. Nothing has been created.',
      },
    };
  }
  // Defensive: an unexpected 2xx that still carries a navigable draft url.
  const fallbackUrl = res?.url ?? res?.edit_url;
  if (fallbackUrl) {
    return {
      status: 'created',
      url: fallbackUrl,
      draft_id: res?.draft_id ?? res?.id ?? res?.item?.id,
      intake_module: intakeModule,
    };
  }
  return { status: 'pending', intake_module: intakeModule };
}


// ---- blog long-form draft (P1) --------------------------------------------

/**
 * The six content types the AI Studio selector exposes. Only `blog_article` is
 * ACTIVE in P1 (the others are on the roadmap, shown disabled so the surface is
 * honest about what it can produce today). `blog_article` is preselected.
 */
export const CONTENT_TYPES = [
  { id: 'blog_article', label: 'Blog / article', active: true },
  { id: 'social_post', label: 'Social post', active: false },
  { id: 'newsletter', label: 'Newsletter', active: false },
  { id: 'landing_page', label: 'Landing page', active: false },
  { id: 'case_study', label: 'Case study', active: false },
  { id: 'product_page', label: 'Product page', active: false },
] as const;

export type ContentTypeId = (typeof CONTENT_TYPES)[number]['id'];

/** The default (preselected) content type — the only one active in P1. */
export const DEFAULT_CONTENT_TYPE: ContentTypeId = 'blog_article';

/** Human label for a content type id (falls back to the id). */
export function contentTypeLabel(id: string): string {
  const found = CONTENT_TYPES.find((t) => t.id === id);
  return found ? found.label : id;
}

/** True iff the content type is producible today (P1 => only blog_article). */
export function isContentTypeActive(id: string): boolean {
  const found = CONTENT_TYPES.find((t) => t.id === id);
  return found ? found.active : false;
}

/**
 * The plain/markdown body to place on the clipboard for "Copy text". Uses the
 * markdown body verbatim (never the HTML) so pasted text stays clean.
 */
export function blogDraftCopyText(draft: Pick<BlogDraft, 'title' | 'body_md'>): string {
  return (draft.body_md ?? '').trim();
}

/** The HTML body to place on the clipboard for "Copy HTML" (uses body_html). */
export function blogDraftCopyHtml(draft: Pick<BlogDraft, 'body_html'>): string {
  return (draft.body_html ?? '').trim();
}

/**
 * SEO-evidence lines for the long-form draft. The server-shaped `seo_evidence`
 * is permissive; keep only rows that carry a summary and expose a label so the
 * view can render them like the proposal evidence.
 */
export function blogSeoEvidenceLines(
  evidence: { summary?: string; source_label?: string; url?: string }[] | undefined,
): { label: string; summary: string; url?: string }[] {
  return (evidence ?? [])
    .filter((e) => e && typeof e.summary === 'string' && e.summary.trim() !== '')
    .map((e) => ({
      label: (e.source_label && e.source_label.trim()) || 'Evidence',
      summary: e.summary as string,
      ...(e.url && typeof e.url === 'string' ? { url: e.url } : {}),
    }));
}

// ---- minimal, dependency-free markdown block parser (pure) -----------------
// The article body is rendered from body_md as SAFE React elements (no
// dangerouslySetInnerHTML, no external sanitizer). body_html is used ONLY for
// the "Copy HTML" clipboard action, never injected into the DOM. This parser is
// block-level (headings / paragraphs / lists / blockquote / rule); inline
// emphasis is applied by the view. Pure + unit-testable.

export type MarkdownBlock =
  | { type: 'heading'; level: 1 | 2 | 3; text: string }
  | { type: 'paragraph'; text: string }
  | { type: 'ul'; items: string[] }
  | { type: 'ol'; items: string[] }
  | { type: 'blockquote'; text: string }
  | { type: 'hr' };

export function parseMarkdownBlocks(md: string): MarkdownBlock[] {
  const lines = (md ?? '').replace(/\r\n/g, '\n').split('\n');
  const blocks: MarkdownBlock[] = [];
  let para: string[] = [];
  let ul: string[] = [];
  let ol: string[] = [];

  const flushPara = () => {
    if (para.length > 0) {
      blocks.push({ type: 'paragraph', text: para.join(' ').trim() });
      para = [];
    }
  };
  const flushUl = () => {
    if (ul.length > 0) {
      blocks.push({ type: 'ul', items: ul.slice() });
      ul = [];
    }
  };
  const flushOl = () => {
    if (ol.length > 0) {
      blocks.push({ type: 'ol', items: ol.slice() });
      ol = [];
    }
  };
  const flushAll = () => {
    flushPara();
    flushUl();
    flushOl();
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (line === '') {
      flushAll();
      continue;
    }
    if (/^(---|\*\*\*|___)$/.test(line)) {
      flushAll();
      blocks.push({ type: 'hr' });
      continue;
    }
    const h = /^(#{1,3})\s+(.*)$/.exec(line);
    if (h) {
      flushAll();
      blocks.push({ type: 'heading', level: h[1].length as 1 | 2 | 3, text: h[2].trim() });
      continue;
    }
    const uli = /^[-*+]\s+(.*)$/.exec(line);
    if (uli) {
      flushPara();
      flushOl();
      ul.push(uli[1].trim());
      continue;
    }
    const oli = /^\d+[.)]\s+(.*)$/.exec(line);
    if (oli) {
      flushPara();
      flushUl();
      ol.push(oli[1].trim());
      continue;
    }
    const bq = /^>\s?(.*)$/.exec(line);
    if (bq) {
      flushAll();
      blocks.push({ type: 'blockquote', text: bq[1].trim() });
      continue;
    }
    flushUl();
    flushOl();
    para.push(line);
  }
  flushAll();
  return blocks;
}

// ---- WordPress DRAFT push (approved blog draft -> connected site) ----------
// The pure logic behind the "Push to WordPress Draft" action on an APPROVED
// blog draft. Extracted here so it is unit-testable under this repo's DOM-free
// convention; WordPressPushControl is a thin view over these helpers.
//
// TWO INVARIANTS, both structural:
//   1. APPROVE != SEND. Approval only makes a draft ELIGIBLE (status 'ready').
//      Nothing is pushed until a SEPARATE, explicit human click. isPushEligible
//      gates eligibility; the push is a distinct call the user must trigger.
//   2. DRAFT-ONLY. The push body carries ONLY draft_id + target_id (+ optional
//      push_options) — there is NO status/publish field to send, and the server
//      hard-codes the WordPress status to 'draft'. isDraftOnlyResult refuses to
//      show anything but a draft-family outcome as success.
//
// This is deliberately NOT a copilot control verb: it is a capability on an
// already-approved draft, so the connector-gate invariant on
// controlsForPhase / FORBIDDEN_CONTROL_VERBS (no publish/schedule/send in the
// conversation) is completely untouched.

/** The phases of the explicit draft-push affordance. */
export type PushPhase =
  | 'idle' // eligible; no connected site chosen yet
  | 'selected' // a connected site is chosen — ready for the explicit push
  | 'pushing' // the explicit push is in flight
  | 'pushed' // the draft now exists remotely as a WordPress DRAFT
  | 'error'; // the push failed — nothing was published

/** A connected site a draft can be pushed to (view-model projection). */
export interface PushableTarget {
  id: number;
  label: string;
  siteUrl: string;
}

/** A draft is ELIGIBLE to push only when it is APPROVED (status 'ready') and
 *  persisted (has a real id). Approval alone pushes NOTHING. */
export function isPushEligible(draft: { id: number; status: string }): boolean {
  return Number(draft.id) > 0 && draft.status === 'ready';
}

/** True iff a connection advertises the push_drafts capability. */
function hasPushDrafts(caps: Record<string, unknown> | null | undefined): boolean {
  if (!caps) return false;
  const v = caps['push_drafts'];
  return v === true || v === 1 || v === '1';
}

/**
 * The connected sites a draft can be pushed to: ACTIVE targets that advertise
 * the push_drafts capability. A disabled or non-push-capable connection is never
 * offered as a push destination.
 */
export function selectablePushTargets(
  connections: {
    id: number;
    site_label?: string;
    site_url?: string;
    status?: string;
    capabilities?: Record<string, unknown> | null;
  }[],
): PushableTarget[] {
  return (connections ?? [])
    .filter(
      (c) =>
        c &&
        Number(c.id) > 0 &&
        (c.status ?? 'active') === 'active' &&
        hasPushDrafts(c.capabilities),
    )
    .map((c) => ({
      id: Number(c.id),
      label:
        (c.site_label && c.site_label.trim()) ||
        (c.site_url && c.site_url.trim()) ||
        'Site #' + c.id,
      siteUrl: (c.site_url ?? '').trim(),
    }));
}

/** True iff an explicit push is allowed RIGHT NOW: an eligible draft AND a
 *  chosen, valid target. Never true on approval alone. */
export function canPush(
  draft: { id: number; status: string },
  targetId: number | null,
): boolean {
  return isPushEligible(draft) && typeof targetId === 'number' && targetId > 0;
}

/**
 * The POST body for POST /v1/connector/publications. DRAFT-ONLY: it carries
 * ONLY draft_id + target_id (+ optional push_options). There is NO status or
 * publish field — the server forces the WordPress status to 'draft'.
 */
export function buildPublicationBody(
  draftId: number,
  targetId: number,
  pushOptions: Record<string, unknown> = {},
): { draft_id: number; target_id: number; push_options?: Record<string, unknown> } {
  const body: {
    draft_id: number;
    target_id: number;
    push_options?: Record<string, unknown>;
  } = { draft_id: Number(draftId), target_id: Number(targetId) };
  if (pushOptions && Object.keys(pushOptions).length > 0) {
    body.push_options = pushOptions;
  }
  return body;
}

/** Result of a completed draft push (server response projection). */
export interface PushResult {
  ok: boolean;
  remotePostId: string | null;
  remoteStatus: string; // 'draft' — never 'publish'
  editUrl?: string;
}

/**
 * DRAFT-ONLY guard on a server response: accept ONLY a draft-family status as a
 * genuine success, so a mis-built/compromised server that returned a PUBLISHED
 * post can never be presented as a success here (defence in depth behind the
 * server's own draft-only enforcement).
 */
export function isDraftOnlyResult(remoteStatus: string): boolean {
  const s = (remoteStatus || '').toLowerCase();
  return s === 'draft' || s === 'auto-draft' || s === 'pending_create';
}

/** The confirmation line after a successful DRAFT push — always states it is a
 *  draft and that nothing was published. */
export function pushSuccessNote(result: PushResult, siteLabel: string): string {
  const where = siteLabel.trim() !== '' ? siteLabel.trim() : 'the connected site';
  const id = result.remotePostId ? ' (post #' + result.remotePostId + ')' : '';
  return (
    'Saved as a DRAFT in ' +
    where +
    id +
    '. Nothing was published — open it in WordPress → Posts → Drafts to review and publish it yourself.'
  );
}
